import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  Truck,
  Route,
  FileText,
  GitCompareArrows,
  PackagePlus,
  Mail,
  ShoppingCart,
  Clock,
  PackageCheck,
  CheckSquare,
  Receipt,
  Wallet,
  LogOut,
  Menu,
  KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/mitarbeiter", label: "Mitarbeiter", icon: Users, testId: "nav-mitarbeiter" },
  { to: "/fahrzeuge", label: "Fahrzeuge", icon: Truck, testId: "nav-fahrzeuge" },
  { to: "/ft-touren", label: "FT-Touren", icon: Route, testId: "nav-ft" },
  { to: "/tagestouren", label: "Tagestouren", icon: FileText, testId: "nav-tagestouren" },
  { to: "/tagestouren-abgleich", label: "TT-Abgleich", icon: GitCompareArrows, testId: "nav-tt-abgleich" },
  { to: "/verladung-entladung", label: "Verladung/Entladung", icon: PackagePlus, testId: "nav-ve" },
  { to: "/quickmail", label: "Quickmail", icon: Mail, testId: "nav-quickmail" },
  { to: "/aldi", label: "Aldi-Touren", icon: ShoppingCart, testId: "nav-aldi" },
  { to: "/spedition", label: "Spedition", icon: Clock, testId: "nav-spedition" },
  { to: "/epk", label: "EPK / Picks", icon: PackageCheck, testId: "nav-epk" },
  { to: "/sonstige-kosten", label: "Sonstige Kosten", icon: Receipt, testId: "nav-expenses" },
  { to: "/fahrerloehne", label: "Fahrerlöhne", icon: Wallet, testId: "nav-payroll" },
  { to: "/monatsabschluss", label: "Monatsabschluss", icon: CheckSquare, testId: "nav-closing" },
];

function NavList({ user, onLogout, onChangePassword, onItemClick }) {
  return (
    <>
      <div className="px-6 py-5 border-b border-neutral-200">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">RS Service 24 AG</div>
        <div className="text-xl font-semibold tracking-tight">FO NIVARA</div>
        <div className="text-xs text-neutral-500 mt-0.5">7 Days Controlling</div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onItemClick}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-[#0055FF]/10 text-[#0055FF] font-medium"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-neutral-200 p-4">
        <div className="text-xs text-neutral-500 mb-2">Angemeldet als</div>
        <div className="text-sm font-medium truncate" data-testid="current-user-email">
          {user?.email || "—"}
        </div>
        <Button variant="outline" size="sm" className="mt-3 w-full" onClick={onChangePassword} data-testid="change-password-button">
          <KeyRound className="h-3.5 w-3.5 mr-2" />
          Passwort ändern
        </Button>
        <Button variant="outline" size="sm" className="mt-2 w-full" onClick={onLogout} data-testid="logout-button">
          <LogOut className="h-3.5 w-3.5 mr-2" />
          Abmelden
        </Button>
      </div>
    </>
  );
}


function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (next !== confirm) {
      toast.error("Neues Passwort und Bestätigung stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Passwort erfolgreich geändert.");
      setCurrent(""); setNext(""); setConfirm("");
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Fehler beim Ändern des Passworts.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Passwort ändern</DialogTitle>
          <DialogDescription>
            Mindestens 8 Zeichen. Wir empfehlen eine Kombination aus Gross-/Kleinbuchstaben, Zahlen und Sonderzeichen.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Aktuelles Passwort</Label>
            <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" required />
          </div>
          <div className="space-y-1">
            <Label>Neues Passwort</Label>
            <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" required minLength={8} />
          </div>
          <div className="space-y-1">
            <Label>Neues Passwort bestätigen</Label>
            <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" required />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
            <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="cp-submit">
              {busy ? "Speichere…" : "Passwort ändern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleChangePassword = () => {
    setMobileOpen(false);
    setCpOpen(true);
  };

  const currentPage = NAV.find((n) => n.to === location.pathname)?.label || "FO NIVARA";

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-neutral-200 bg-white flex-col" data-testid="sidebar">
        <NavList user={user} onLogout={handleLogout} onChangePassword={handleChangePassword} />
      </aside>

      {/* Mobile header + sheet */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-neutral-200 px-4 h-14 flex items-center justify-between" data-testid="mobile-header">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button" aria-label="Menü öffnen">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 flex flex-col">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Hauptmenü der Anwendung</SheetDescription>
            <NavList user={user} onLogout={handleLogout} onChangePassword={handleChangePassword} onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex flex-col items-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 leading-none">FO NIVARA</div>
          <div className="text-sm font-semibold leading-tight">{currentPage}</div>
        </div>
        <div className="w-9" />
      </div>

      <main className="flex-1 min-w-0 lg:pt-0 pt-14">
        <Outlet />
      </main>

      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
    </div>
  );
}
